// @ts-nocheck
import { splitCamelCase } from "@insite/client-framework/Common/StringHelpers";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import FieldDefinition from "@insite/client-framework/Types/FieldDefinition";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { markForFocusLater } from "@insite/mobius/Overlay/helpers/focusManager";
import { BreakpointKey } from "@insite/mobius/utilities/breakpointMediaQueries";
import FlexWrapper, { FlexWrapperPresentationProps } from "@store/Library/Components/Toolkit/FlexWrapper";
import { headingStyles } from "@store/Library/Components/Toolkit/Heading";
import PrinterFill from "@store/Library/Components/Toolkit/Icons/Printer/PrinterFill";
import OrderActions, { OrderActionsPresentationProps } from "@store/Library/Components/Toolkit/Order/OrderActions";
import OrderStickyAction, {
    orderStickyActionBreakpoint,
    OrderStickyActionProps,
} from "@store/Library/Components/Toolkit/Order/OrderStickyAction";
import Button, { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Button";
import OverflowMenu, { OverflowMenuPresentationProps } from "@store/MobiusOverrides/OverflowMenu/OverflowMenu";
import { linkGetActionLinkProps } from "@store/Themes/common/props/link";
import ga from "@toolkit/Framework/Common/GoogleAnalytics";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import useIsBreakpoint from "@toolkit/Framework/Common/Hooks/useIsBreakpoint";
import {
    DataOption,
    DataOptions,
    getDataOptionsFieldDefinition,
    getDataOptionValue,
} from "@toolkit/Framework/Common/Utilities/dataOptions";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { KeysOfType, PartialExcept } from "@toolkit/Framework/Types/Common";
import React, { MouseEventHandler, ReactElement, useMemo } from "react";
import { css } from "styled-components";

/* Store Custom Code - Start */
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { useLocation } from "@toolkit/Framework/Store/Data/Pages/PageHooks";
/* Store Custom Code - End */

const enum fields {
    displayType = "displayType",
    actionsOrder = "actionsOrder",
}

type DisplayTypeOption = DataOption<keyof DisplayTypeOptionMapper>;
interface DisplayTypeOptionMapper {
    button: DisplayTypeOption;
    buttonInline: DisplayTypeOption;
    link: DisplayTypeOption;
    linkOverflow: DisplayTypeOption;
    overflow: DisplayTypeOption;
}
type DisplayTypeOptions = DataOptions<DisplayTypeOption> & DisplayTypeOptionMapper;

const displayTypeOptions: DisplayTypeOptions = {
    button: { id: "button", displayName: "Buttons", isDefault: true },
    buttonInline: { id: "buttonInline", displayName: "Buttons (Inline)" },
    link: { id: "link", displayName: "Links" },
    linkOverflow: { id: "linkOverflow", displayName: "Links (Mobile Overflow)" },
    overflow: { id: "overflow", displayName: "Overflow Menu" },
};

type StateOption = DataOption<KeysOfType<Required<OrderActionSetMapperItem>, boolean>>;
interface StateOptionMapper {
    overflow: StateOption;
    sticky: StateOption;
}
type StateOptions = DataOptions<StateOption> & StateOptionMapper;

const stateOptions: StateOptions = {
    overflow: { id: "overflow", displayName: "Overflow (Inline Only)", value: "isOverflow" },
    sticky: { id: "sticky", displayName: "Sticky (Mobile Only)", value: "isSticky" },
};

const getStateName = (name: string) => `${name}State`;
const getDisplayName = (name: string) => splitCamelCase(name.replace(/^show\s*/i, ""));

export type OrderActionSetBaseFieldDefinition = PartialExcept<
    Omit<FieldDefinition, "fieldType" | "editorTemplate">,
    "name"
>;

export type OrderActionSetFieldDefinition = Omit<OrderActionSetBaseFieldDefinition, "sortOrder"> & {
    defaultState?: keyof StateOptionMapper;
};

export const orderActionSetFieldDefinitions = (
    definitions: Array<OrderActionSetFieldDefinition>,
    sharedDefinition?: Partial<OrderActionSetBaseFieldDefinition>,
) => {
    let sortOrder = sharedDefinition?.sortOrder;
    const orderDefaultValue: HasFields[] = [];

    return [
        {
            ...getDataOptionsFieldDefinition(displayTypeOptions),
            name: fields.displayType,
            displayName: "Display Type",
            ...sharedDefinition,
            sortOrder: sortOrder ? ++sortOrder : undefined,
            hideEmptyOption: true,
        },
        ...definitions.reduce((currentResult, definition, index) => {
            const isLast = index === definitions.length - 1;
            const { name, displayName, tooltip, defaultState } = definition;
            orderDefaultValue.push({ fields: { name, displayName } });

            return [
                ...currentResult,
                {
                    fieldType: "General",
                    editorTemplate: "CheckboxField",
                    defaultValue: true,
                    ...sharedDefinition,
                    ...definition,
                    sortOrder: sortOrder ? ++sortOrder : undefined,
                    tooltip:
                        tooltip || sharedDefinition?.tooltip || isLast
                            ? "Note: Some actions may not appear regardless of these settings if the user is not allowed to use them."
                            : undefined,
                },
                {
                    ...getDataOptionsFieldDefinition(stateOptions),
                    name: getStateName(name),
                    displayName: getDisplayName(getStateName(displayName || name)),
                    defaultValue: defaultState,
                    isVisible: x => {
                        if (x.fields[name]) {
                            const value = getDataOptionValue(displayTypeOptions, x.fields.displayType);

                            if (value === "button" || value === "buttonInline") {
                                return true;
                            }
                        }

                        return false;
                    },
                    ...sharedDefinition,
                    sortOrder: sortOrder ? ++sortOrder : undefined,
                    tab: {
                        displayName: "Advanced",
                        sortOrder: 1,
                    },
                },
            ] as FieldDefinition[];
        }, [] as FieldDefinition[]),
        {
            name: fields.actionsOrder,
            displayName: "Actions Order",
            editorTemplate: "ListField",
            defaultValue: orderDefaultValue,
            getDisplay: (x: HasFields) => getDisplayName(x.fields.displayName || x.fields.name),
            hideAdd: true,
            hideEdit: true,
            hideDelete: true,
            fieldType: "General",
            fieldDefinitions: [
                {
                    name: "name",
                    editorTemplate: "TextField",
                    defaultValue: "",
                },
            ],
            ...sharedDefinition,
            sortOrder: sortOrder ? ++sortOrder : undefined,
            tooltip: '"Sticky" actions will always appear first no matter the selected order.',
        },
    ] as FieldDefinition[];
};

export interface OrderActionSetMapperPresets {
    print: OrderActionSetMapperItem;
}

export interface OrderActionSetMapperItem {
    button?: JSX.Element;
    buttonStyles?: ButtonPresentationProps;
    href?: string;
    isDisabled?: boolean;
    key?: string;
    label?: ReactElement | string;
    link?: JSX.Element;
    linkStyles?: LinkPresentationProps;
    isLoading?: boolean;
    onClick?: MouseEventHandler;
    preset?: keyof OrderActionSetMapperPresets;
    isVisible?: boolean;
    isSticky?: boolean;
    testSelector?: string;
    isOverflow?: boolean;
    field?: string;
}

export type OrderActionSetMapper = Array<OrderActionSetMapperItem>;

export interface OrderActionSetPresentationProps extends ExpandStyles<OrderActionSetStyles> {}

export interface OrderActionSetProps extends OrderActionSetPresentationProps, Partial<HasFields> {
    actions: OrderActionSetMapper;
    overflowBreakpoint?: BreakpointKey;
    displayType?: keyof DisplayTypeOptionMapper;
}

export interface OrderActionSetStyles {
    button?: ButtonPresentationProps;
    inlineOrderActions?: OrderActionsPresentationProps;
    link?: LinkPresentationProps;
    linkFlexWrapper?: FlexWrapperPresentationProps;
    orderActions?: OrderActionsPresentationProps;
    orderStickyAction?: OrderStickyActionProps;
    overflowMenu?: OverflowMenuPresentationProps;
    printLink?: LinkPresentationProps;
    printButton?: ButtonPresentationProps;
}

export const actionSetStyles: OrderActionSetStyles = {
    link: linkGetActionLinkProps(),
    linkFlexWrapper: {
        gap: headingStyles.itemFlexWrapper?.gap,
        verticalGap: headingStyles.itemFlexWrapper?.verticalGap,
    },
    inlineOrderActions: {
        gap: pxToRem(8),
        verticalGap: pxToRem(8),
        extendedStyles: {
            flexWrapper: {
                item: {
                    css: css`
                        width: auto;
                    `,
                },
            },
        },
    },
    printLink: { icon: { iconProps: { src: PrinterFill } } },
    printButton: { variant: "tertiary" },
};

const OrderActionSet: React.FC<OrderActionSetProps> = ({
    actions: baseActions,
    fields: widgetFields,
    displayType: baseDisplayType = "button",
    overflowBreakpoint = orderStickyActionBreakpoint,
    extendedStyles,
    overridedStyles,
}) => {
    const baseStyles = useExpandProps(actionSetStyles, extendedStyles, overridedStyles);
    const isOverflowBreakpoint = useIsBreakpoint(overflowBreakpoint, "down");
    const isStickyBreakpoint = useIsBreakpoint(orderStickyActionBreakpoint, "down");
    const displayType = getDataOptionValue(displayTypeOptions, widgetFields?.[fields.displayType] || baseDisplayType);
    const isButtons = displayType === "button";
    const isInlineButtons = displayType === "buttonInline";
    const isOverflowMenu = displayType === "overflow" || (displayType === "linkOverflow" && isOverflowBreakpoint);
    const actionsOrder: HasFields[] | undefined = widgetFields?.[fields.actionsOrder];
    const trackPrint = ga.use.trackPrint();
    const history = useHistory(); // Store Custom Code

    const location = useLocation(); // Store Custom Code

    const printClickHandler = () => {
        trackPrint();
        /* Store Custom Code - Start */

        const queryParams = parseQueryString<{
            invoiceNumber?: string;
            orderNumber?: string;
        }>(location.search);

        const { invoiceNumber, orderNumber } = queryParams;

        if (invoiceNumber) {
            window.open(`/Invoice/GetPdf?invoiceNumber=${invoiceNumber}`, "_blank", "noopener,noreferrer");
        } else if (orderNumber) {
            window.open(`/Order/GetPdf?orderNumber=${orderNumber}`, "_blank", "noopener,noreferrer");
        } else {
            openPrintDialog();
        }

        /* Store Custom Code - End */
    };

    const styles = useMemo(() => {
        if (!isInlineButtons) {
            return baseStyles;
        }

        return mergeProps<OrderActionSetStyles>(
            {
                orderStickyAction: {
                    extendedStyles: {
                        flexWrapper: {
                            content: {
                                css: css`
                                    flex-wrap: nowrap;
                                `,
                            },
                        },
                    },
                },
            },
            baseStyles,
        );
    }, [baseStyles, isInlineButtons]);

    const presets = useMemo((): OrderActionSetMapperPresets => {
        return {
            print: {
                key: "print",
                label: translate("Print"),
                onClick: () => printClickHandler(),
                linkStyles: styles.printLink,
                buttonStyles: styles.printButton,
            },
        };
    }, [styles.printLink, styles.printButton]);

    const actions = useMemo(() => {
        const result: OrderActionSetMapper = [];

        baseActions.forEach(x => {
            if (x.isVisible === false) {
                return;
            }

            const action = x.preset ? mergeProps(presets[x.preset], x) : { ...x };

            if (widgetFields && x.field) {
                if (widgetFields[x.field] === false) {
                    return;
                }

                const stateFieldValue = getDataOptionValue(stateOptions, widgetFields[getStateName(x.field)]);
                if (stateFieldValue) {
                    action[stateFieldValue] = true;
                }
            }

            action.button = action.button || (
                <Button
                    {...mergeProps(styles.button, action.buttonStyles)}
                    key={action.key}
                    onClick={action.onClick}
                    disabled={action.isDisabled || action.isLoading}
                    data-test-selector={action.testSelector}
                    isLoading={action.isLoading}
                >
                    {action.label}
                </Button>
            );

            action.link = action.link || (
                <Link
                    {...mergeProps(styles.link, action.linkStyles)}
                    key={action.key}
                    onClick={action.onClick}
                    href={!action.onClick ? action?.href : undefined}
                    disabled={action.isDisabled || action.isLoading}
                    data-test-selector={action.testSelector}
                >
                    {action.label}
                </Link>
            );

            if (actionsOrder && x.field) {
                const index = actionsOrder.findIndex(x => x.fields.name === action.field);
                if (index !== -1) {
                    result[index] = action;
                } else {
                    result.push(action);
                }
            } else {
                result.push(action);
            }
        });

        return result;
    }, [baseActions, presets]);

    if (!actions.length) {
        return null;
    }

    if (isOverflowMenu) {
        return (
            <OverflowMenu onOpen={markForFocusLater} {...styles.overflowMenu}>
                {actions.map(x => x.link)}
            </OverflowMenu>
        );
    }

    if (isButtons || isInlineButtons) {
        const stickyButtons: Array<OrderActionSetMapperItem["button"]> = [];
        const otherButtons: Array<OrderActionSetMapperItem["button"]> = [];
        const overflowLinks: Array<OrderActionSetMapperItem["link"]> = [];

        const showOverflowMenu = isInlineButtons && actions.some(x => x.isOverflow);

        actions.forEach(x => {
            if (x.isSticky && isStickyBreakpoint) {
                stickyButtons.push(x.button);
            } else if (showOverflowMenu && (x.isOverflow || isOverflowBreakpoint)) {
                overflowLinks.push(x.link);
            } else {
                otherButtons.push(x.button);
            }
        });

        return (
            <OrderActions {...(isInlineButtons ? styles.inlineOrderActions : styles.orderActions)}>
                {stickyButtons.length ? (
                    <OrderStickyAction {...styles.orderStickyAction}>{stickyButtons}</OrderStickyAction>
                ) : null}
                {otherButtons}
                {overflowLinks.length ? (
                    <OverflowMenu onOpen={markForFocusLater} {...styles.overflowMenu}>
                        {overflowLinks}
                    </OverflowMenu>
                ) : null}
            </OrderActions>
        );
    }

    return <FlexWrapper {...styles.linkFlexWrapper}>{actions.map(x => x.link)}</FlexWrapper>;
};

export default OrderActionSet;
